import React from "react";
import ReactDOMServer from "react-dom/server";
import { useTranslation } from "react-i18next";
import { styled } from "goober";

import { readState } from "@/__main__/app-state.mjs";
import { getAugmentDetail } from "@/game-tft/get-augment-info.mjs";
import { autoScaleUp } from "@/game-tft/InGameShared.jsx";
import ParsedDescription from "@/game-tft/ParsedDescription.jsx";
import StaticTFT from "@/game-tft/static.mjs";
import { TooltipStats } from "@/game-tft/UnitToolTip.jsx";
import useSetByParam from "@/game-tft/use-set-by-param.mjs";
import { getLocale } from "@/util/i18n-helper.mjs";
import { useLazyTooltip } from "@/util/tooltip.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

// Static
const legendTranslation = ["tft:legend", "Legend"];

const TooltipContainer = styled("div")`
  position: absolute;
  overflow: hidden;
  top: 0;
  width: 220px;
  height: fit-content;
  font-size: var(--sp-3) !important;
  color: var(--shade1);
  background: var(--shade10);
  border-radius: var(--br);
  filter: drop-shadow(0 3px var(--sp-2) var(--shade10-50));
  padding: var(--sp-1) !important;
  transform: translate(-50%, 0) scale(${autoScaleUp()});
  transform-origin: top center;

  hr {
    border: none;
    border-top: 1px solid var(--shade6);
  }

  .augmentHoverImg {
    width: 70px;
    height: 70px;
  }

  .augment-tooltip-top {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    padding: var(--sp-2);

    .augment-name {
      color: var(--shade0);
    }
  }

  .augment-tooltip-description {
    padding: 0 var(--sp-2);
  }

  .hero-augment {
    .augment-tooltip-top {
      height: var(--sp-25);
      padding: var(--sp-1);
      background: var(--shade10);
    }
  }
`;

// Component
const AugmentTooltip = ({
  children,
  augment,
  style,
  className,
  stats,
  hideDescription,
  matchSet,
}) => {
  const { t } = useTranslation();
  const state = useSnapshot(readState);
  const curSet = useSetByParam();

  const tooltipRef = useLazyTooltip(() => {
    const locale = getLocale();
    const set = matchSet ?? curSet;
    const localizedAugments = (() => {
      const localizedAugments = state.tft[set]?.localizedAugments?.[locale];
      return {
        ...localizedAugments?.hero,
        ...localizedAugments?.tier1,
        ...localizedAugments?.tier2,
        ...localizedAugments?.tier3,
      };
    })(set);
    const augments = state.tft[set]?.augments;
    const isNotReady = !augments || !set || !augment;
    const augmentInfo = getAugmentDetail(augment, augments);
    const name = localizedAugments[augment]?.name ?? augmentInfo.name;
    const description =
      localizedAugments[augment]?.description ?? augmentInfo.description;
    if (isNotReady) return "";
    const mapper = {
      name,
      description,
      set,
      type: augmentInfo.type,
      augment: augment,
      imgSrc: StaticTFT.getAugmentImage(augment, set),
    };
    mapper.inner =
      mapper.name +
      (mapper.augment.includes("_Legend_") ? ` - ${t(legendTranslation)}` : "");
    const tooltipRenderedTemplate = ReactDOMServer.renderToStaticMarkup(
      <TooltipContainer>
        <div className="augment-tooltip-container">
          <div className="augment-tooltip">
            <div className="augment-tooltip-top">
              <img
                id="augment-img"
                className="augmentHoverImg"
                alt="augment-img"
                src={mapper.imgSrc}
              />
              <div className="augment-name">
                <span>{mapper.inner}</span>
              </div>
            </div>
            <TooltipStats
              avgPlace={stats?.avgPlace}
              pickRate={stats?.pickRate}
              avgPlaceImprovement={stats?.avgPlaceImprovement}
              avgPlaceWith={stats?.avgPlaceWith}
              avgPlaceWithout={stats?.avgPlaceWithout}
              locale={locale}
              t={t}
            />
            {!hideDescription ? (
              <>
                <hr />
                <div className="augment-tooltip-description">
                  <ParsedDescription
                    text={mapper.description}
                    iconSize={"var(--sp-4)"}
                  />
                </div>
              </>
            ) : (
              ""
            )}
          </div>
        </div>
      </TooltipContainer>,
    );
    return tooltipRenderedTemplate;
  });

  return (
    <div
      ref={tooltipRef}
      data-place="bottom"
      style={style}
      className={className}
    >
      {children}
    </div>
  );
};

export default AugmentTooltip;
